import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import axios from "axios";
import { useAuthStore } from "../../context/useAuthStore";
import TextInput from "./TextInput";
import { FiSearch } from "react-icons/fi";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

interface Subject {
  subjectId: string;
  subjectCode: string;
  subjectName: string;
}

interface EnrolledSubject {
  subjectId: string;
  subjectCode: string;
  subjectName: string;
}

type ManageSubjectsProps = {
  onEnrollSuccess?: () => void;
};

const ManageSubjects = ({ onEnrollSuccess }: ManageSubjectsProps) => {
  const { user, token, clearSession } = useAuthStore();
  const [form, setForm] = useState({
    subjectIds: [] as string[],
  });

  const [loading, setLoading] = useState(false);
  const [subjectData, setSubjectData] = useState<Subject[]>([]);
  const [enrolledSubjects, setEnrolledSubjects] = useState<EnrolledSubject[]>(
    []
  );
  const [subjectLoading, setSubjectLoading] = useState(false);

  // Search query state
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const fetchEnrolledSubjects = async () => {
      if (!user?.userId) return;

      try {
        const res = await axios.get(
          `${API_BASE_URL}/subscription?userId=${user.userId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          }
        );

        if (res.data?.success && Array.isArray(res.data.success)) {
          setEnrolledSubjects(res.data.success);
        }
      } catch (error) {
        // Handle session expiration
        if (axios.isAxiosError(error)) {
          const errorMessage =
            error.response?.data?.message ||
            "Failed to fetch enrolled subjects";

          // Check for session expiration
          if (
            errorMessage.includes("Session expired") ||
            error.response?.status === 401
          ) {
            clearSession("expired");
          }
        }
        setEnrolledSubjects([]);
      }
    };

    fetchEnrolledSubjects();
  }, [user?.userId, token, clearSession]);

  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        setSubjectLoading(true);
        const res = await axios.get(`${API_BASE_URL}/subject`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        });

        if (res.data?.success && Array.isArray(res.data.success)) {
          setSubjectData(res.data.success);
        } else {
          setSubjectData([]);
        }
      } catch (error) {
        // Handle session expiration
        if (axios.isAxiosError(error)) {
          const errorMessage =
            error.response?.data?.message || "Failed to fetch subjects";

          // Check for session expiration
          if (
            errorMessage.includes("Session expired") ||
            error.response?.status === 401
          ) {
            clearSession("expired");
          }
        }
        setSubjectData([]);
      } finally {
        setSubjectLoading(false);
      }
    };

    fetchSubjects();
  }, [token, clearSession]);

  const isSubjectEnrolled = (subjectId: string): boolean => {
    return enrolledSubjects.some((sub) => sub.subjectId === subjectId);
  };

  const handleSubjectToggle = (subjectId: string) => {
    setForm((prevForm) => {
      const newSubjectIds = prevForm.subjectIds.includes(subjectId)
        ? prevForm.subjectIds.filter((id) => id !== subjectId)
        : [...prevForm.subjectIds, subjectId];

      return { ...prevForm, subjectIds: newSubjectIds };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation for no subjects selected
    if (form.subjectIds.length === 0) {
      toast.error("Please select at least one subject.");
      return;
    }

    if (!user?.userId) {
      toast.error("User information not found.");
      return;
    }

    setLoading(true);

    try {
      const payload = form.subjectIds.map((subjectId) => ({
        userId: user.userId,
        subjectId: subjectId,
        subjectCode: subjectData.find((sub) => sub.subjectId === subjectId)
          ?.subjectCode,
      }));

      const res = await axios.post(`${API_BASE_URL}/subscription`, payload, {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.data?.success) {
        toast.success("Subjects enrolled successfully!");
        setForm({ subjectIds: [] });

        const newEnrolledSubjects = subjectData.filter((sub) =>
          form.subjectIds.includes(sub.subjectId)
        );
        setEnrolledSubjects((prev) => [...prev, ...newEnrolledSubjects]);

        onEnrollSuccess?.();
      }
    } catch (error) {
      // Handle session expiration
      if (axios.isAxiosError(error)) {
        const errorMessage =
          error.response?.data?.message ||
          "An error occurred while enrolling the subjects";

        // Check for session expiration
        if (
          errorMessage.includes("Session expired") ||
          error.response?.status === 401
        ) {
          clearSession("expired");
        } else {
          toast.error(errorMessage);
        }
      } else {
        toast.error("An error occurred while enrolling the subjects.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Combine both available and enrolled subjects
  const availableSubjects = subjectData.filter(
    (sub) => !isSubjectEnrolled(sub.subjectId)
  );
  const combinedSubjects = [...availableSubjects, ...enrolledSubjects];

  // Apply the search filter across the combined subjects
  const filteredSubjects = combinedSubjects.filter((sub) =>
    sub.subjectName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <section className="subjectSection">
      <h2 className="subjectTitle">Start your subject selection</h2>
      <div className="borderLine"></div>

      {/* Search Input */}
      <div className="mb-4 w-full">
        <TextInput
          id="searchQuery"
          name="searchQuery"
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by subject name..."
          icon={FiSearch}
          disabled={loading}
          errorMessage={""}
          required={false}
          error={false}
        />
      </div>

      {/* Subject List (Including both available and enrolled subjects) */}
      <div className="subjectList">
        {subjectLoading ? (
          <p>Loading subjects...</p>
        ) : filteredSubjects.length === 0 ? (
          <p className="text-xl font-semibold text-red-500">
            No subjects available for "
            <span className="font-bold text-red-600">{searchQuery}</span>"
          </p>
        ) : (
          <form onSubmit={handleSubmit}>
            <ul className="list-none space-y-2 grid grid-cols-1 md:grid-cols-3 sm:grid-cols-2 gap-2 mb-4">
              {filteredSubjects.map((sub) => {
                const isChecked = form.subjectIds.includes(sub.subjectId);
                const isEnrolled = isSubjectEnrolled(sub.subjectId);
                return (
                  <li
                    key={sub.subjectId}
                    className={`listOfQuestionItem ${
                      isEnrolled
                        ? "bg-gray-300 text-gray-700"
                        : isChecked
                        ? "bg-myLabelText text-white cursor-pointer"
                        : "bg-white text-myLabelText cursor-pointer"
                    } rounded-md px-2 py-1`}
                  >
                    <label
                      htmlFor={`checkbox-${sub.subjectId}`}
                      className={`flex items-center space-x-2 w-full ${
                        isEnrolled ? "cursor-not-allowed" : "cursor-pointer"
                      }`}
                    >
                      <input
                        type="checkbox"
                        id={`checkbox-${sub.subjectId}`}
                        checked={isChecked}
                        onChange={() => handleSubjectToggle(sub.subjectId)}
                        className="hidden"
                        disabled={isEnrolled}
                      />
                      <span className="flex items-center space-x-2">
                        {isChecked && (
                          <span className="text-green-500 font-bold">✔</span>
                        )}
                        <span>{sub.subjectName}</span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>

            {/* Submit Button */}
            <button
              type="submit"
              className={`defaultButton ${
                loading || form.subjectIds.length === 0 ? "disabled" : ""
              }`}
              disabled={loading || form.subjectIds.length === 0}
            >
              {loading ? "Please wait..." : "Enroll Subjects"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
};

export default ManageSubjects;
